'use client';

import React, { useCallback } from 'react';
import { cn } from '@/lib/utils';

export interface ToolbarProps {
  /** Accessible name for the toolbar (required). */
  label: string;
  /** Arrow-key axis. Defaults to horizontal (←→). */
  orientation?: 'horizontal' | 'vertical';
  children: React.ReactNode;
  className?: string;
}

/**
 * Accessible toolbar wrapper.
 *
 * Adds `role="toolbar"` + `aria-label` and moves focus across focusable
 * children with ←→ (horizontal) or ↑↓ (vertical) plus Home/End. Children keep
 * their normal `<button>` semantics; disabled/hidden buttons are skipped.
 */
export function Toolbar({
  label,
  orientation = 'horizontal',
  children,
  className,
}: ToolbarProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const forward = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
      const backward = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
      if (![forward, backward, 'Home', 'End'].includes(e.key)) return;

      const container = e.currentTarget;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

      if (focusables.length === 0) return;
      const current = focusables.indexOf(document.activeElement as HTMLElement);

      let nextIndex = current;
      if (e.key === forward) nextIndex = current + 1;
      else if (e.key === backward) nextIndex = current - 1;
      else if (e.key === 'Home') nextIndex = 0;
      else if (e.key === 'End') nextIndex = focusables.length - 1;

      nextIndex = Math.max(0, Math.min(focusables.length - 1, nextIndex));
      if (nextIndex !== current) {
        e.preventDefault();
        focusables[nextIndex]?.focus();
      }
    },
    [orientation],
  );

  return (
    <div
      role="toolbar"
      aria-label={label}
      aria-orientation={orientation}
      onKeyDown={handleKeyDown}
      className={className}
    >
      {children}
    </div>
  );
}

export default Toolbar;
